import React, { useEffect, useMemo, useState } from "react";

import _ from "lodash";
import cx from "classnames";

import styles from "./scenarios.module.scss";
import Thumbnail from "src/components/thumbnail";
import { useSelector } from "react-redux";
import Actions from "src/components/actionbar";
import Button from "src/components/button";
import { animated, useSpring, useTransition } from "@react-spring/web";
import useMeasure from "react-use-measure";
import { SimulationContext } from "../../context";
import { Simulation } from "src/types/coaching/simulation";
import { useDispatch } from "react-redux";
import { invalidateShuffleObjectivesState, invalidateShuffleOrganizationState, invalidateShufflePersonaState, shuffleObjectives, shuffleOrganization, shufflePersona, updateSimulationProperty } from "src/redux/actions/coaching/simulation";
import TextareaAutosize from "react-autosize-textarea";
import Text from "src/components/text";

interface ScenarioSettingsProps {
  simulation: Simulation;
}

export const ScenarioSettings: React.FC<ScenarioSettingsProps> = ({ simulation }) => {
  const dispatch = useDispatch();

  const simulationContext = React.useContext(SimulationContext);

  const description = useMemo(() => {
    return simulation.description || "";
  }, [simulation]);

  const { shufflingOrganization, shuffledOrganization } = useSelector((state: any) => {
    const shufflingOrganization = _.get(state, `coaching.simulations.edit.shuffle.organization.loading`);
    const shuffledOrganization = _.get(state, `coaching.simulations.edit.shuffle.organization.item`);

    return {
      shufflingOrganization,
      shuffledOrganization
    }
  }, _.isEqual);

  const { shufflingPersona, shuffledPersona } = useSelector((state: any) => {
    const shufflingPersona = _.get(state, `coaching.simulations.edit.shuffle.persona.loading`);
    const shuffledPersona = _.get(state, `coaching.simulations.edit.shuffle.persona.item`);

    return {
      shufflingPersona,
      shuffledPersona
    }
  }, _.isEqual);

  const { shufflingObjectives, shuffledObjectives } = useSelector((state: any) => {
    const shufflingObjectives = _.get(state, `coaching.simulations.edit.shuffle.objectives.loading`);
    const shuffledObjectives = _.get(state, `coaching.simulations.edit.shuffle.objectives.item`);

    return {
      shufflingObjectives,
      shuffledObjectives
    }
  }, _.isEqual);

  const shuffling = shufflingOrganization || shufflingPersona || shufflingObjectives;

  const handleEditOrganizationInfo = () => {
    if (simulationContext.editOrganizationInfo) {
      simulationContext.editOrganizationInfo(true);
    }
  }

  const handleEditPersona = () => {
    if (simulationContext.editPersona) {
      simulationContext.editPersona(true);
    }
  }

  const handleEditObjectives = () => {
    if (simulationContext.editObjectives) {
      simulationContext.editObjectives(true);
    }
  }

  const handleShuffle = () => {
    handleShufflePersona();
    handleShuffleOrganization();
    handleShuffleObjectives();
  }

  const handleShufflePersona = () => {
    dispatch(shufflePersona(simulation!.id!));
  }

  const handleShuffleOrganization = () => {
    dispatch(shuffleOrganization(simulation!.id!));
  }

  const handleShuffleObjectives = () => {
    dispatch(shuffleObjectives(simulation!.id!));
  }

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "description", value }));
  }

  useEffect(() => {
    if (shuffledPersona) {
      dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "persona", value: shuffledPersona }));
      dispatch(invalidateShufflePersonaState(simulation!.id!));
    }
  }, [shuffledPersona]);

  useEffect(() => {
    if (shuffledOrganization) {
      dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "organization", value: shuffledOrganization }));
      dispatch(invalidateShuffleOrganizationState(simulation!.id!));
    }
  }, [shuffledOrganization]);

  useEffect(() => {
    if (shuffledObjectives) {
      dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "objectives", value: shuffledObjectives }));
      dispatch(invalidateShuffleObjectivesState(simulation!.id!));
    }
  }, [shuffledObjectives]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <TextareaAutosize
          className={styles.summary}
          rows={1}
          maxRows={4}
          value={description}
          placeholder={
            'Describe the scenario to set the stage for your reps.\nInclude helpful context and key pointers to guide them through the simulation.\nMake it clear so they can confidently navigate the experience!...'
          }
          spellCheck={true}
          onChange={handleDescriptionChange}
          onPointerEnterCapture={() => { }}
          onPointerLeaveCapture={() => { }}
        />
        <div className={styles.actions}>
          <Button className={cx(styles.action, styles.shuffle)} icon="paperflite-shuffle" text={shuffling ? 'Shuffling...' : 'Shuffle'} onClick={handleShuffle}></Button>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.setting}>
          <div className={styles.type}>Company profile</div>
          <ShuffleAnimatedContainer shuffle={shufflingOrganization} placeholder={OrganizationInfoPlaceholder}>
            <OrganizationInfoCard simulation={simulation} onEdit={handleEditOrganizationInfo} onShuffle={handleShuffleOrganization} />
          </ShuffleAnimatedContainer>
        </div>
        <div className={styles.setting}>
          <div className={styles.type}>Buyer profile</div>
          <ShuffleAnimatedContainer shuffle={shufflingPersona} placeholder={PersonaPlaceholder}>
            <PersonaCard simulation={simulation} onEdit={handleEditPersona} onShuffle={handleShufflePersona} />
          </ShuffleAnimatedContainer>
        </div>
        <div className={styles.setting}>
          <div className={styles.type}>Buyer needs</div>
          <ShuffleAnimatedContainer shuffle={shufflingObjectives} placeholder={ObjectivesPlaceholder}>
            <ObjectivesCard simulation={simulation} onEdit={handleEditObjectives} onShuffle={handleShuffleObjectives} />
          </ShuffleAnimatedContainer>
        </div>
      </div>
    </div>
  );
}

interface ShuffleAnimatedContainerProps {
  children: React.ReactNode;
  shuffle: boolean;
  placeholder: React.ComponentType;
}

const ShuffleAnimatedContainer: React.FC<ShuffleAnimatedContainerProps> = ({ shuffle, children, placeholder }) => {
  const [height, setHeight] = useState(100); // Placeholder height, adjust as needed
  const [cardRef, cardBounds] = useMeasure();

  const elementTransition = useTransition(!shuffle, {
    from: { transform: "translateY(0%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(-100%)", opacity: 1 },
    onRest: () => {

    },
  });

  const placeholderTransition = useTransition(shuffle, {
    from: { transform: "translateY(100%)", opacity: 1 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(0%)", opacity: 0 }
  });

  useEffect(() => {
    if (!shuffle) {
      setHeight(cardBounds.height);
    }
  }, [shuffle, cardBounds]);

  return (
    <div className={cx(styles.shuffleContainer)}>
      {elementTransition((style, item) =>
        item ? (
          <animated.div ref={cardRef} style={style} className={cx(styles.wrapper)}>
            {children}
          </animated.div>
        ) : false)}
      {placeholderTransition((style, item) =>
        item ? (
          <animated.div style={style} className={cx(styles.wrapper)}>
            <ShufflePlaceholderContainer placeholder={placeholder} height={400} />
          </animated.div>
        ) : false)}
    </div>
  );
}

interface ShufflePlaceholderContainerProps {
  height: number;
  placeholder: React.ComponentType;
}

const ShufflePlaceholderContainer: React.FC<ShufflePlaceholderContainerProps> = ({ height, placeholder }) => {
  const cards = 8;
  const [index, setIndex] = React.useState(0);
  const order = useMemo(() => {
    return _.shuffle(_.times(cards));
  }, []);

  const [props, api] = useSpring(() => ({
    delay: 800,
    from: { transform: "translateY(0%)" },
    to: async (next) => {
      const orderValue = order[index];
      const translateY = orderValue * -100;
      await next({ transform: `translateY(${translateY}%)` });
    },
  }), [index]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % cards);
    }, 1600); // Change every second

    return () => clearInterval(interval);
  }, []);

  return (
    <animated.div className={cx(styles.shufflePlaceholders)} style={{ ...props, height }}>
      {_.times(cards, (index) => (
        <div key={index} className={cx(styles.wrapper)} style={{ height }}>
          {React.createElement(placeholder)}
        </div>
      ))}
    </animated.div>
  );
}

interface OrganizationInfoCardProps {
  simulation: Simulation;
  onEdit: () => void;
  onShuffle: () => void;
}

export const OrganizationInfoCard: React.FC<OrganizationInfoCardProps> = ({ simulation, onEdit, onShuffle }) => {
  const organization = useMemo(() => {
    return simulation.organization;
  }, [simulation]);

  return (
    <div className={cx(styles.card, styles.organizationInfo)}>
      <div className={styles.header}>
        <div className={styles.name}>
          <i className={cx(styles.icon, "paperflite-multiple-documents")} />
          {organization?.name || "No Name"}
        </div>
      </div>
      <div className={styles.body}>
        <Text className={styles.summary} 
          text={organization?.description || ""} 
          lines={10} 
          tooltip={false} 
        />
      </div>
      <div className={styles.actions}>
        <Actions.Row>
          <Actions.IconButton
            icon="paperflite-edit"
            tooltip="Edit"
            onClick={onEdit}
          />
          <Actions.IconButton
            icon="paperflite-shuffle"
            tooltip="Shuffle"
            onClick={onShuffle}
          />
        </Actions.Row>
      </div>
    </div>
  );
}

const OrganizationInfoPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.organizationInfo, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.body}>
        <div className={styles.summary}>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
        </div>
      </div>
    </div>
  );
}

interface PersonaCardProps {
  simulation: Simulation;
  onEdit: () => void;
  onShuffle: () => void;
}

export const PersonaCard: React.FC<PersonaCardProps> = ({ simulation, onEdit, onShuffle }) => {
  const persona = useMemo(() => {
    return simulation.persona;
  }, [simulation]);

  return (
    <div className={cx(styles.card, styles.persona)}>
      <div className={styles.header}>
        <Thumbnail media={persona?.icon} defaultImage="/images/assets/default_asset.jpg" className={styles.icon} />
        <div className={styles.name}>
          {persona?.name || "No Name"}
        </div>
      </div>
      <div className={styles.body}>
        <Text className={styles.summary} 
          text={persona?.description || ""} 
          lines={10} 
          tooltip={false} 
        />
      </div>
      <div className={styles.actions}>
        <Actions.Row>
          <Actions.IconButton
            icon="paperflite-edit"
            tooltip="Edit"
            onClick={onEdit}
          />
          <Actions.IconButton
            icon="paperflite-shuffle"
            tooltip="Shuffle"
            onClick={onShuffle}
          />
        </Actions.Row>
      </div>
    </div>
  );
}

const PersonaPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.persona, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.body}>
        <div className={styles.summary}>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
        </div>
      </div>
    </div>
  );
}

interface ObjectivesCardProps {
  simulation: Simulation;
  onEdit: () => void;
  onShuffle: () => void;
}

export const ObjectivesCard: React.FC<ObjectivesCardProps> = ({ simulation, onEdit, onShuffle }) => {
  const objectives = useMemo(() => {
    return simulation.objectives;
  }, [simulation]);

  return (
    <div className={cx(styles.card, styles.objectives)}>
      <div className={styles.header}>
        <div className={styles.name}>
          <i className={cx(styles.icon, "paperflite-document")} />
          Objectives
        </div>
      </div>
      <div className={styles.body}>
        <Text className={styles.summary} 
          text={objectives?.description || ""} 
          lines={10}
          tooltip={false} 
        />
      </div>
      <div className={styles.actions}>
        <Actions.Row>
          <Actions.IconButton
            icon="paperflite-edit"
            tooltip="Edit"
            onClick={onEdit}
          />
          <Actions.IconButton
            icon="paperflite-shuffle"
            tooltip="Shuffle"
            onClick={onShuffle}
          />
        </Actions.Row>
      </div>
    </div>
  );
}

const ObjectivesPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.objectives, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.body}>
        <div className={styles.summary}>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
        </div>
      </div>
    </div>
  );
}